/** @format */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

type Direction = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  direction?: Direction;
  maxWidth?: number;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  direction = 'left',
  maxWidth = 240
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualDirection, setActualDirection] = useState<Direction>(direction);

  const updatePosition = (dir: Direction) => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (dir) {
      case 'top':
        top = rect.top + window.scrollY;
        left = rect.left + rect.width / 2 + window.scrollX;
        break;

      case 'bottom':
        top = rect.bottom + window.scrollY;
        left = rect.left + rect.width / 2 + window.scrollX;
        break;

      case 'left':
        top = rect.top + rect.height / 2 + window.scrollY;
        left = rect.left + window.scrollX;
        break;

      case 'right':
        top = rect.top + rect.height / 2 + window.scrollY;
        left = rect.right + window.scrollX;
        break;
    }

    setPosition({ top, left });
  };

  const showTooltip = () => {
    setActualDirection(direction);
    updatePosition(direction);
    setVisible(true);
  };

  const hideTooltip = () => {
    setVisible(false);
  };

  // 🔥 Auto-flip logic
  useEffect(() => {
    if (!visible || !tooltipRef.current) return;

    const rect = tooltipRef.current.getBoundingClientRect();
    let newDirection = actualDirection;

    if (actualDirection === 'top' && rect.top < 0) {
      newDirection = 'bottom';
    } else if (
      actualDirection === 'bottom' &&
      rect.bottom > window.innerHeight
    ) {
      newDirection = 'top';
    } else if (actualDirection === 'left' && rect.left < 0) {
      newDirection = 'right';
    } else if (actualDirection === 'right' && rect.right > window.innerWidth) {
      newDirection = 'left';
    }

    if (newDirection !== actualDirection) {
      setActualDirection(newDirection);
      updatePosition(newDirection);
    }
  }, [visible, position, actualDirection]);

  const renderTooltip = () => {
    const style: React.CSSProperties = {
      position: 'absolute',
      top: position.top,
      left: position.left,
      zIndex: 999999,
      maxWidth
    };

    return (
      <div
        ref={tooltipRef}
        className={`tooltip-portal direction-${actualDirection}`}
        style={style}
      >
        {content}
      </div>
    );
  };

  return (
    <>
      <div
        ref={wrapperRef}
        style={{ display: 'inline-block' }}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {children}
      </div>

      {visible && ReactDOM.createPortal(renderTooltip(), document.body)}
    </>
  );
};

export default Tooltip;
